import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import { CommandMenu } from 'src/dialogs/CommandMenu/CommandMenu';
import { useCommandMenuOptions } from 'src/hooks/api/useCommandMenuOptions';
import { IconsProps } from 'src/svgs/Viagogo';
import { COMMAND_OPTION_TYPE_TO_CID } from 'src/utils/constants/contentIdMaps';
import { CommandOptionType } from 'src/WebApiController';

import { Content } from '../ContentContext';

export interface CommandOption {
  id: CommandOptionType;
  onSelect: (value: string) => void;
}

export interface CommandOptionDisplay {
  id: string;
  label: React.ReactNode;
  onSelect: () => void;
  shortcut?: string;
  icon?: (props: IconsProps) => JSX.Element;
}

type CommandMenuContextType = {
  isCommandMenuOpened: boolean;
  setIsCommandMenuOpened: React.Dispatch<React.SetStateAction<boolean>>;
  registerCommand: ({
    command,
    disabled,
  }: {
    command: CommandOption;
    disabled?: boolean;
  }) => void;
  unregisterCommand: ({
    commandId,
    disabled,
  }: {
    commandId: string;
    disabled?: boolean;
  }) => void;
  getCommands: () => CommandOptionDisplay[];
};

type CommandMenuContextProviderProps = { children: ReactNode };

const CommandMenuContext = createContext<CommandMenuContextType>({
  isCommandMenuOpened: false,
  setIsCommandMenuOpened: () => {},
  registerCommand: () => {},
  unregisterCommand: () => {},
  getCommands: () => [],
});

export const CommandMenuContextProvider = ({
  children,
}: CommandMenuContextProviderProps) => {
  const [isCommandMenuOpened, setIsCommandMenuOpened] = useState(false);
  const [commands, setCommands] = useState<
    Record<CommandOptionType, CommandOption>
  >({} as Record<CommandOptionType, CommandOption>);
  const { data, isLoading } = useCommandMenuOptions();

  const registerCommand = useCallback(
    ({ command, disabled }: { command: CommandOption; disabled?: boolean }) => {
      if (disabled) {
        return;
      }

      setCommands((prevCommands) => {
        return {
          ...prevCommands,
          [command.id]: command,
        };
      });
    },
    []
  );

  const unregisterCommand = useCallback(
    ({ commandId, disabled }: { commandId: string; disabled?: boolean }) => {
      if (disabled) {
        return;
      }

      setCommands((prevCommands) => {
        const newCommands = { ...prevCommands };
        delete newCommands[commandId as CommandOptionType];
        return newCommands;
      });
    },
    []
  );

  const getCommands = useCallback(() => {
    return !isLoading && data
      ? Object.entries(commands)
          .filter(([id]) => id in data)
          .map(([id, command]) => {
            const shortcut = data[id as CommandOptionType];
            const label = COMMAND_OPTION_TYPE_TO_CID[id as CommandOptionType];
            return {
              ...command,
              label: <Content id={label} />,
              shortcut: shortcut,
            } as CommandOptionDisplay;
          })
      : [];
  }, [commands, data, isLoading]);

  return (
    <CommandMenuContext.Provider
      value={{
        isCommandMenuOpened,
        setIsCommandMenuOpened,
        registerCommand,
        unregisterCommand,
        getCommands,
      }}
    >
      <CommandMenu />
      {children}
    </CommandMenuContext.Provider>
  );
};

export const useCommandMenuContext = () => useContext(CommandMenuContext);
